import React from 'react';
import { BlockMath, InlineMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import LinkButton from './LinkButton';
import Typography from '../../components/common/Typography';

export const LINK_PATTERN = /\[(.*?)\]\(([^)]+)\)/g; // 서버에서 보장하는 링크 패턴

/**
 * 마크다운 파싱 함수
 * @param {string} text - 마크다운 텍스트
 * @returns {JSX.Element} - 파싱된 마크다운 텍스트
 */
export const parseMarkdown = text => {
  const blocks = processBlocks(text);

  return blocks.map((block, index) => {
    // BlockMath 처리
    if (block.type === 'math-block') {
      const formula = block.content.match(/\\\[([\s\S]*?)\\\]/)?.[1];
      if (!formula) return null;

      return (
        <div key={`block-${index}`} className="answerFormula">
          <BlockMath math={formula.trim()} />
        </div>
      );
    }

    // 내용 전체를 처리
    const content = block.content
      .split('\n')
      .map(line => {
        // 코드블럭(```) 제거 처리
        if (line.trim().startsWith('```')) {
          return '';
        }
        // 수식을 포함하는 줄에서 - 제거
        if (line.includes('\\(') || line.includes('\\[')) {
          // 줄 시작 부분의 - 와 공백 제거
          return line.replace(/^\s*-\s*/, '').trim();
        }
        return line;
      })
      .join('\n');

    // 인라인 수식을 찾는 정규식 (줄바꿈 포함)
    const regex = /(\\\([\s\S]*?\\\))/g;
    const parts = content.split(regex).filter(Boolean);

    return (
      <div key={`block-${index}`}>
        {parts.map((part, pIndex) => {
          // 수식 처리
          if (part.startsWith('\\(') && part.endsWith('\\)')) {
            const formula = part.slice(2, -2).trim();

            // \begin{...} 이 포함된 수식은 BlockMath로 렌더링
            if (formula.includes('\\begin{')) {
              return (
                <div key={`math-${pIndex}`} className="answerFormula">
                  <BlockMath math={formula} />
                </div>
              );
            }

            // 일반 인라인 수식은 InlineMath로 렌더링
            return (
              <div key={`math-${pIndex}`} className="answerFormula">
                <InlineMath math={formula} />
              </div>
            );
          }

          // 수식 외 텍스트 처리
          const textLines = part.split('\n').map((line, lineIndex) => {
            // 수평선(---) 처리
            if (line.match(/^\s*-{3,}\s*$/)) {
              return (
                <hr
                  key={`hr-${pIndex}-${lineIndex}`}
                  style={{
                    border: 'none',
                    borderTop: '1px solid #e0e0e0',
                    margin: 0,
                  }}
                />
              );
            }

            // 헤딩(#) 처리
            const headingMatch = line.match(/^\s*(#{1,6})\s+(.*)$/);
            if (headingMatch) {
              const level = headingMatch[1].length;
              const sizes = {
                1: { fontSize: '20px', marginTop: '16', marginBottom: '5' },
                2: { fontSize: '18px', marginTop: '14', marginBottom: '4' },
                3: { fontSize: '16px', marginTop: '12', marginBottom: '3' },
                4: { fontSize: '15px', marginTop: '10', marginBottom: '2' },
                5: { fontSize: '14px', marginTop: '8', marginBottom: '2' },
                6: { fontSize: '13px', marginTop: '6', marginBottom: '1' },
              };
              const style = sizes[level];
              const cleanText = headingMatch[2].replace(/\*{2,}/g, '').trim(); // 연속된 ** 문자 제거 (bold 제거)

              return (
                <div
                  key={`heading-${pIndex}-${lineIndex}`}
                  style={{
                    fontSize: style.fontSize,
                    margin: `${style.marginTop}px 0 ${style.marginBottom}px 0`,
                    fontWeight: 700,
                  }}>
                  {cleanText}
                </div>
              );
            }

            // 링크와 bold 텍스트 처리
            return <div key={`text-${pIndex}-${lineIndex}`}>{parseLineWithLinksAndBold(line)}</div>;
          });

          return textLines;
        })}
      </div>
    );
  });
};

const processBlocks = text => {
  const blocks = [];
  let currentBlock = '';
  let insideMath = false;
  let mathType = null;

  const lines = text.split('\n');

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // 한 줄에 전체 수식이 있는 경우 (\[ \])만 math-block으로 처리
    if (line.includes('\\[') && line.includes('\\]')) {
      if (currentBlock) {
        blocks.push({ type: 'text', content: currentBlock });
        currentBlock = '';
      }
      blocks.push({ type: 'math-block', content: line });
      continue;
    }

    // 인라인 수식 (\( \))은 text 블록으로 처리
    if (line.includes('\\(') && line.includes('\\)')) {
      currentBlock += (currentBlock ? '\n' : '') + line;
      continue;
    }

    // 수식 블록 시작 확인
    if (line.includes('\\[') && !insideMath) {
      if (currentBlock) {
        blocks.push({ type: 'text', content: currentBlock });
        currentBlock = '';
      }
      insideMath = true;
      mathType = 'block';
      currentBlock = line;
      continue;
    }

    // 수식 블록 종료 확인
    if (line.includes('\\]') && insideMath && mathType === 'block') {
      currentBlock += '\n' + line;
      blocks.push({ type: 'math-block', content: currentBlock });
      currentBlock = '';
      insideMath = false;
      mathType = null;
      continue;
    }

    // 수식 블록 내부
    if (insideMath) {
      currentBlock += '\n' + line;
      continue;
    }

    // 빈 줄 존재 시, 현재 블록을 저장하고 새로운 블록 시작
    if (line.trim() === '') {
      if (currentBlock) {
        blocks.push({ type: 'text', content: currentBlock });
        currentBlock = '';
      }
      continue;
    }

    // 일반 텍스트
    currentBlock += (currentBlock ? '\n' : '') + line;
  }

  if (currentBlock) {
    blocks.push({ type: 'text', content: currentBlock });
  }

  return blocks;
};

// 한 줄에서 링크와 bold 텍스트를 파싱하는 함수
const parseLineWithLinksAndBold = line => {
  // 서버에서 보장하는 링크 패턴: [title](url) - title 안에 어떤 문자든 가능
  const linkPattern = LINK_PATTERN;
  const parts = [];
  let lastIndex = 0;
  let match;

  // 모든 링크를 찾아서 처리
  while ((match = linkPattern.exec(line)) !== null) {
    // 링크 앞의 텍스트 처리
    if (match.index > lastIndex) {
      const beforeText = line.slice(lastIndex, match.index);
      if (beforeText.trim()) {
        parts.push(processBoldText(beforeText));
      }
    }

    // 링크 버튼 생성
    const linkText = match[1];
    const linkUrl = match[2];
    parts.push(
      <LinkButton key={`link-${match.index}`} url={linkUrl} title={linkText} width="auto">
        {linkText}
      </LinkButton>,
    );

    lastIndex = match.index + match[0].length;
  }

  // 마지막 링크 이후의 텍스트 처리
  if (lastIndex < line.length) {
    const afterText = line.slice(lastIndex);
    if (afterText.trim()) {
      parts.push(processBoldText(afterText));
    }
  }

  // 링크가 없는 경우 전체 텍스트를 bold 처리
  if (parts.length === 0) {
    return processBoldText(line);
  }

  return parts;
};

// Bold 텍스트를 처리하는 함수
const processBoldText = text => {
  const boldParts = text.split(/(\*\*.*?\*\*)/g);

  return boldParts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={`bold-${i}`} style={{ fontWeight: 700 }}>
          {part.slice(2, -2)}
        </strong>
      );
    }
    return part;
  });
};

// 링크 문자열을 파싱해서 버튼 컴포넌트로 변환하는 함수 (시스템 메시지용)
export const parseLinkButtons = text => {
  if (!text) {
    return <span></span>;
  }

  const linkPattern = LINK_PATTERN;
  const linkButtons = [];
  let match;

  // 링크 버튼들 추출
  let buttonIndex = 0;
  while ((match = linkPattern.exec(text)) !== null) {
    linkButtons.push(
      <LinkButton
        key={`link-${match.index}-${buttonIndex}`}
        url={match[2]}
        title={match[1]}>
        {match[1]}
      </LinkButton>,
    );
    buttonIndex++;
  }

  // 링크가 없으면 전체 텍스트만 반환
  if (linkButtons.length === 0) {
    return text;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px'}}>
      <Typography
        {...{ variant: 'body2Medium', color: 'GrayScale-600', style: { whiteSpace: 'nowrap' } }}>
        관련 사이트를 하나씩 확인하고 있어요...
      </Typography>
      <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>{linkButtons}</div>
    </div>
  );
};
